import { useState, useMemo, useRef, forwardRef } from 'react';
import Papa from 'papaparse';
import { BarChart3, LineChart, PieChart, Download } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field, Textarea, Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';

const SAMPLE = `quarter,revenue,users
Q1,180,12000
Q2,225,15500
Q3,260,18200
Q4,310,22500`;

type ChartType = 'bar' | 'line' | 'pie';
const COLORS = ['#6366f1', '#22d3ee', '#a855f7', '#f59e0b', '#ec4899', '#10b981', '#ef4444', '#0ea5e9'];

export function QuickChart() {
  const [csv, setCsv] = useState(SAMPLE);
  const [type, setType] = useState<ChartType>('bar');
  const [labelKey, setLabelKey] = useState('quarter');
  const [valueKey, setValueKey] = useState('revenue');
  const [title, setTitle] = useState('Quarterly Revenue');
  const svgRef = useRef<SVGSVGElement>(null);

  const parsed = useMemo(() => {
    if (!csv.trim()) return { headers: [] as string[], rows: [] as Record<string, string>[] };
    const r = Papa.parse<Record<string, string>>(csv, { header: true, skipEmptyLines: true });
    return { headers: (r.meta.fields ?? []) as string[], rows: r.data };
  }, [csv]);

  const series = useMemo(() => {
    return parsed.rows.map((r) => ({
      label: String(r[labelKey] ?? ''),
      value: parseFloat(r[valueKey] ?? '0') || 0,
    }));
  }, [parsed, labelKey, valueKey]);

  const total = series.reduce((s, d) => s + d.value, 0);
  const max = Math.max(1, ...series.map((d) => d.value));

  function exportSvg() {
    if (!svgRef.current) return;
    const svgStr = new XMLSerializer().serializeToString(svgRef.current);
    const blob = new Blob([svgStr], { type: 'image/svg+xml' });
    saveAs(blob, `${title || 'chart'}.svg`);
    toast.success('SVG saved');
  }

  function exportPng() {
    if (!svgRef.current) return;
    const svg = svgRef.current;
    const svgStr = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    const svgBlob = new Blob([svgStr], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = svg.clientWidth * 2;
      c.height = svg.clientHeight * 2;
      const ctx = c.getContext('2d')!;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(img, 0, 0, c.width, c.height);
      c.toBlob((b) => {
        if (b) saveAs(b, `${title || 'chart'}.png`);
        URL.revokeObjectURL(url);
        toast.success('PNG saved');
      });
    };
    img.src = url;
  }

  return (
    <div>
      <ToolHeader
        toolId="quick-chart"
        title="Quick chart"
        description="Paste a CSV, pick a label and value column, get a clean chart. Export PNG or SVG."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:order-2 space-y-3">
          <Field label="Chart type">
            <Tabs value={type} onValueChange={(v) => setType(v as ChartType)}>
              <TabsList className="w-full">
                <TabsTrigger value="bar" className="flex-1">
                  <BarChart3 size={11} className="mr-1" />
                  Bar
                </TabsTrigger>
                <TabsTrigger value="line" className="flex-1">
                  <LineChart size={11} className="mr-1" />
                  Line
                </TabsTrigger>
                <TabsTrigger value="pie" className="flex-1">
                  <PieChart size={11} className="mr-1" />
                  Pie
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </Field>
          <Field label="Title">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <Field label="Label column">
            <Select
              value={labelKey}
              onChange={(e) => setLabelKey(e.target.value)}
              options={parsed.headers.map((h) => ({ value: h, label: h }))}
            />
          </Field>
          <Field label="Value column">
            <Select
              value={valueKey}
              onChange={(e) => setValueKey(e.target.value)}
              options={parsed.headers.map((h) => ({ value: h, label: h }))}
            />
          </Field>
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border">
            <Button size="sm" variant="ghost" onClick={exportSvg}>
              <Download size={11} />
              SVG
            </Button>
            <Button size="sm" variant="primary" onClick={exportPng}>
              <Download size={11} />
              PNG
            </Button>
          </div>
        </Card>

        <div className="lg:col-span-2 lg:order-1 space-y-3">
          <Card className="!p-0 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
              {title}
            </div>
            <div className="p-6 bg-white">
              {type === 'bar' && <BarSvg ref={svgRef} data={series} max={max} />}
              {type === 'line' && <LineSvg ref={svgRef} data={series} max={max} />}
              {type === 'pie' && <PieSvg ref={svgRef} data={series} total={total} />}
            </div>
          </Card>

          <Card className="!p-0">
            <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
              CSV source
            </div>
            <Textarea
              mono
              value={csv}
              onChange={(e) => setCsv(e.target.value)}
              className="!border-0 !rounded-t-none min-h-[180px] !bg-transparent focus-visible:!ring-0 text-[12px]"
            />
          </Card>
        </div>
      </div>
    </div>
  );
}

interface BarProps {
  data: { label: string; value: number }[];
  max: number;
}
const BarSvg = forwardRef<SVGSVGElement, BarProps>(({ data, max }, ref) => {
  const w = 720;
  const h = 360;
  const padX = 40;
  const padBottom = 60;
  const padTop = 20;
  const chartW = w - padX * 2;
  const chartH = h - padBottom - padTop;
  const slot = chartW / Math.max(1, data.length);
  const barW = slot * 0.7;
  const gap = slot * 0.3;
  return (
    <svg ref={ref} viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
      {[0, 0.25, 0.5, 0.75, 1].map((t) => (
        <g key={t}>
          <line x1={padX} x2={w - padX} y1={padTop + chartH * (1 - t)} y2={padTop + chartH * (1 - t)} stroke="#e5e7eb" strokeWidth="1" />
          <text x={padX - 8} y={padTop + chartH * (1 - t) + 4} textAnchor="end" fill="#6b7280" fontSize="10" fontFamily="JetBrains Mono, monospace">
            {(max * t).toFixed(0)}
          </text>
        </g>
      ))}
      {data.map((d, i) => {
        const barH = (d.value / max) * chartH;
        const x = padX + i * slot + gap / 2;
        const y = padTop + chartH - barH;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={barH} fill={COLORS[i % COLORS.length]} rx={2} />
            <text x={x + barW / 2} y={y - 6} textAnchor="middle" fill="#374151" fontSize="10" fontWeight="600" fontFamily="JetBrains Mono, monospace">
              {d.value}
            </text>
            <text x={x + barW / 2} y={padTop + chartH + 18} textAnchor="middle" fill="#6b7280" fontSize="11" fontFamily="Inter, sans-serif">
              {d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
});
BarSvg.displayName = 'BarSvg';

const LineSvg = forwardRef<SVGSVGElement, BarProps>(({ data, max }, ref) => {
  const w = 720;
  const h = 360;
  const padX = 40;
  const padBottom = 60;
  const padTop = 20;
  const chartW = w - padX * 2;
  const chartH = h - padBottom - padTop;
  const points = data.map((d, i) => {
    const x = padX + (i / Math.max(1, data.length - 1)) * chartW;
    const y = padTop + chartH - (d.value / max) * chartH;
    return { x, y, ...d };
  });
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const fill = `${path} L ${padX + chartW} ${padTop + chartH} L ${padX} ${padTop + chartH} Z`;
  return (
    <svg ref={ref} viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
      <defs>
        <linearGradient id="lineg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={COLORS[0]} stopOpacity="0.3" />
          <stop offset="100%" stopColor={COLORS[0]} stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0, 0.25, 0.5, 0.75, 1].map((t) => (
        <line key={t} x1={padX} x2={w - padX} y1={padTop + chartH * (1 - t)} y2={padTop + chartH * (1 - t)} stroke="#e5e7eb" strokeWidth="1" />
      ))}
      <path d={fill} fill="url(#lineg)" />
      <path d={path} fill="none" stroke={COLORS[0]} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r={4} fill={COLORS[0]} />
          <text x={p.x} y={padTop + chartH + 18} textAnchor="middle" fill="#6b7280" fontSize="11" fontFamily="Inter, sans-serif">
            {p.label}
          </text>
        </g>
      ))}
    </svg>
  );
});
LineSvg.displayName = 'LineSvg';

interface PieProps {
  data: { label: string; value: number }[];
  total: number;
}
const PieSvg = forwardRef<SVGSVGElement, PieProps>(({ data, total }, ref) => {
  const w = 360;
  const h = 360;
  const cx = w / 2;
  const cy = h / 2;
  const r = 130;
  let cumulative = 0;
  const slices = data.map((d, i) => {
    const a0 = (cumulative / Math.max(1, total)) * Math.PI * 2;
    cumulative += d.value;
    const a1 = (cumulative / Math.max(1, total)) * Math.PI * 2;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const x0 = cx + r * Math.sin(a0);
    const y0 = cy - r * Math.cos(a0);
    const x1 = cx + r * Math.sin(a1);
    const y1 = cy - r * Math.cos(a1);
    const path = `M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} Z`;
    const labelAngle = (a0 + a1) / 2;
    const lx = cx + r * 0.65 * Math.sin(labelAngle);
    const ly = cy - r * 0.65 * Math.cos(labelAngle);
    return { path, color: COLORS[i % COLORS.length], lx, ly, label: d.label, value: d.value, pct: (d.value / Math.max(1, total)) * 100 };
  });
  return (
    <svg ref={ref} viewBox={`0 0 ${w + 200} ${h}`} className="w-full h-auto">
      {slices.map((s, i) => (
        <g key={i}>
          <path d={s.path} fill={s.color} />
          {s.pct > 5 && (
            <text x={s.lx} y={s.ly} textAnchor="middle" fill="#fff" fontSize="11" fontWeight="600">
              {s.pct.toFixed(0)}%
            </text>
          )}
        </g>
      ))}
      {slices.map((s, i) => (
        <g key={`l-${i}`} transform={`translate(${w + 20}, ${20 + i * 22})`}>
          <rect width="14" height="14" rx="2" fill={s.color} />
          <text x={20} y={11} fontSize="12" fill="#374151" fontFamily="Inter, sans-serif">
            {s.label} · {s.value}
          </text>
        </g>
      ))}
    </svg>
  );
});
PieSvg.displayName = 'PieSvg';

export default QuickChart;
